import React, { useState, useEffect, useRef, useContext } from 'react';
import { UserContext } from '../UserContext'; // Import UserContext
import '../styles/DevChat.css';

// This should ideally be imported from a central config file like /src/config/api.js
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Component for the HTTP Streaming Chat
const HttpChatWindow = ({ assistant, onClose }) => {
    const [messages, setMessages] = useState([{ sender: 'assistant', text: assistant.greeting }]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    // Effect to scroll to the latest message
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputValue.trim()) return;

        const userMessage = { sender: 'user', text: inputValue };
        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsLoading(true);

        // Add a placeholder for the assistant's response
        setMessages(prev => [...prev, { sender: 'assistant', text: '' }]);

        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error("Authentication token not found.");
            }

            const response = await fetch(`${API_BASE_URL}/api/ws/http`, {
                method: 'POST', 
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ message: inputValue })
            });

            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`HTTP error! status: ${response.status} - ${errorBody}`);
            }
            
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            
            let done = false;
            while (!done) {
                const { value, done: streamDone } = await reader.read();
                if (value) {
                    const chunk = decoder.decode(value, { stream: true });
                    setMessages(prev => {
                        const allButLast = prev.slice(0, -1);
                        const lastMessage = prev[prev.length - 1];
                        const updatedLastMessage = {
                            ...lastMessage,
                            text: lastMessage.text + chunk,
                        };
                        return [...allButLast, updatedLastMessage];
                    });
                }
                done = streamDone;
            }

        } catch (error) {
            console.error("Failed to send message:", error);
            setMessages(prev => {
                const allButLast = prev.slice(0, -1);
                const lastMessage = prev[prev.length - 1];
                const updatedLastMessage = {
                    ...lastMessage,
                    text: `Error: ${error.message}`,
                };
                return [...allButLast, updatedLastMessage];
            });
        } finally {
            setIsLoading(false);
        }
    };

    const chatWindowStyle = {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        width: '400px',
        maxWidth: '90vw',
        height: '600px',
        maxHeight: '80vh',
        backgroundColor: 'white',
        border: '1px solid #ddd',
        borderRadius: '10px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        overflow: 'hidden'
    };

    return (
        <div style={chatWindowStyle} className="http-chat-container">
            <div className="http-chat-header" style={{ padding: '0.75rem 1rem', backgroundColor: '#f7f7f7', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>{assistant.name} (HTTP)</h3>
                <button onClick={onClose} className="close-btn" style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
            </div>
            <div className="http-chat-messages" style={{ flex: '1 1 auto', overflowY: 'auto', padding: '1rem' }}>
                {messages.map((msg, index) => (
                    <div key={index} className={`message ${msg.sender}`} style={{ marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
                        <p style={{
                            margin: 0,
                            padding: '0.5rem 0.75rem',
                            borderRadius: '15px',
                            maxWidth: '80%',
                            backgroundColor: msg.sender === 'user' ? '#179da6' : '#e5e5ea',
                            color: msg.sender === 'user' ? 'white' : 'black',
                        }}>
                            {msg.text}
                        </p>
                    </div>
                ))}
                {isLoading && messages[messages.length - 1]?.sender === 'assistant' && messages[messages.length - 1]?.text === '' && (
                    <div className="message assistant" style={{ marginBottom: '0.5rem' }}><p style={{padding: '0.5rem 0.75rem', borderRadius: '15px', backgroundColor: '#e5e5ea', color: 'black'}} className="typing-indicator">...</p></div>
                )}
                <div ref={messagesEndRef} />
            </div>
            <form onSubmit={handleSendMessage} className="http-chat-input-form" style={{ display: 'flex', padding: '1rem', borderTop: '1px solid #ddd', background: '#f7f7f7' }}>
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Type your message..."
                    disabled={isLoading}
                    style={{ flex: '1 1 auto', marginRight: '1rem', padding: '0.75rem', borderRadius: '20px', border: '1px solid #ccc' }}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(e)}
                />
                <button type="submit" disabled={isLoading} style={{ padding: '0.75rem 1.5rem', borderRadius: '20px', border: 'none', background: '#179da6', color: 'white', cursor: 'pointer' }}>Send</button>
            </form>
        </div>
    );
};


const DevChat = () => {
    const [assistants, setAssistants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAssistant, setSelectedAssistant] = useState(null);
    const { userId } = useContext(UserContext);

    useEffect(() => {
        const fetchAssistants = async () => {
            const token = localStorage.getItem('access_token');
            if (!token) {
                setError('Authentication error: No token found.');
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`${API_BASE_URL}/api/agents`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                const assistantsArray = Array.isArray(data) ? data : (data ? [data] : []);
                setAssistants(assistantsArray);

            } catch (e) {
                console.error("Failed to fetch assistants:", e);
                setError('Error: Could not fetch assistants from the API.');
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            fetchAssistants();
        } else {
            setLoading(false);
            setError("Please log in to view assistants.");
        }
    }, [userId]);

    const handleLaunchChat = (assistant) => {
        console.log(`Launching HTTP chat for: ${assistant.name}`);
        setSelectedAssistant(assistant);
    };

    const handleCloseChat = () => {
        setSelectedAssistant(null);
    };

    return (
        <div className="dev-chat-page">
            <div className="container">
                <h1>Assistant Chat Dev Page</h1>
                <p>Select an assistant below to launch the chat widget.</p>
                <div id="assistant-selector">
                    {loading && <div className="loader">Loading assistants...</div>}
                    {error && <div className="loader">{error}</div>}
                    {!loading && !error && (
                         <ul className="assistant-list">
                            {assistants.length > 0 ? (
                                assistants.map(assistant => (
                                    <li 
                                        key={assistant.id} 
                                        className="assistant-item"
                                        onClick={() => handleLaunchChat(assistant)}
                                    >
                                        <strong>{assistant.name}</strong>
                                        <span>ID: {assistant.id}</span>
                                    </li>
                                ))
                            ) : (
                                <p>No assistants found for this account.</p>
                            )}
                        </ul>
                    )}
                </div>
            </div>
            
            {selectedAssistant && (
                <HttpChatWindow assistant={selectedAssistant} onClose={handleCloseChat} />
            )}
        </div>
    );
};

export default DevChat;

